import Color from "color"
import * as flags from "country-flag-icons/react/3x2"
import countries from "i18n-iso-countries"
import arCountries from "i18n-iso-countries/langs/ar.json"
import beCountries from "i18n-iso-countries/langs/be.json"
import deCountries from "i18n-iso-countries/langs/de.json"
import enCountries from "i18n-iso-countries/langs/en.json"
import esCountries from "i18n-iso-countries/langs/es.json"
import frCountries from "i18n-iso-countries/langs/fr.json"
import itCountries from "i18n-iso-countries/langs/it.json"
import jaCountries from "i18n-iso-countries/langs/ja.json"
import koCountries from "i18n-iso-countries/langs/ko.json"
import ltCountries from "i18n-iso-countries/langs/lt.json"
import nlCountries from "i18n-iso-countries/langs/nl.json"
import roCountries from "i18n-iso-countries/langs/ro.json"
import zhCountries from "i18n-iso-countries/langs/zh.json"
import { range } from "lodash"
import _ from "lodash"
import { z } from "zod"

import { CertificateStatus } from "@tfg/types/constants/certificates.constants"
import { type AGECResponses } from "@tfg/types/endpoints/agec"
import { CountryIso3166 } from "@tfg/types/endpoints/library"
import {
  TextContentJSON,
  BulletedListContentSchema,
  FormattedTextContentSchema,
} from "@tfg/types/schemas/communication-studio/components"

import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"

type _TupleOf<T, N extends number, R extends unknown[]> = R["length"] extends N
  ? R
  : _TupleOf<T, N, [T, ...R]>
export type Tuple<T, N extends number> = N extends N
  ? number extends N
    ? T[]
    : _TupleOf<T, N, []>
  : never

export const buildPalette = (color: string): Tuple<string, 10> => {
  return range(10)
    .map((i) => i - 4)
    .map((i) => i / 5)
    .map((i) => Color(color).lighten(i).hex()) as Tuple<string, 10>
}

export const isFlagAvailable = (
  country: CountryIso3166
): country is CountryIso3166 & keyof typeof flags => {
  return Object.keys(flags).includes(country)
}

export type getActivitiesType = ReturnType<typeof getActivities>[number]

export const countriesTranslations = [
  arCountries,
  beCountries,
  deCountries,
  enCountries,
  esCountries,
  frCountries,
  itCountries,
  jaCountries,
  koCountries,
  ltCountries,
  nlCountries,
  roCountries,
  zhCountries,
]

export const getActivities = (
  agecData: AGECResponses.GenericAgecData,
  standardsToBeShown: number[]
) => {
  countriesTranslations.forEach((translation) => {
    countries.registerLocale(translation)
  })

  const res = agecData.agecActivityCountries.map((value) => {
    const { currentLanguage } = useGetTranslationsContext()
    const country = value.country.country
    if (!country || !isFlagAvailable(country)) return null
    const countryName = countries.getName(country, currentLanguage)

    const allCertificates = agecData.operations.find((op) => {
      return op.activityName == value.activityName
    })
    const activityCertificates = new Set(allCertificates?.certificates ?? [])

    const filteredCertificates = Array.from(activityCertificates).filter(
      (certificate) =>
        standardsToBeShown.includes(certificate.standardId) &&
        certificate.status === CertificateStatus.PROCESSED
    )
    return {
      activity: {
        name: value.activityName,
      },
      country: {
        name: countryName,
        flag: flags[country] ?? undefined,
      },
      certificates: filteredCertificates,
    }
  })
  return res
}

// Type guard functions for ContentBlock textContent field
export const isTextContentJSONArray = (
  textContent: unknown
): textContent is TextContentJSON[] => {
  return (
    Array.isArray(textContent) &&
    textContent.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "text" in item &&
        "type" in item &&
        typeof item.text === "string" &&
        typeof item.type === "string"
    )
  )
}

export const isBulletedListContent = (
  textContent: unknown
): textContent is z.infer<typeof BulletedListContentSchema> => {
  return (
    typeof textContent === "object" &&
    textContent !== null &&
    "items" in textContent &&
    "style" in textContent &&
    Array.isArray((textContent as any).items) &&
    typeof (textContent as any).style === "string"
  )
}

export const isFormattedTextContent = (
  textContent: unknown
): textContent is z.infer<typeof FormattedTextContentSchema> => {
  return (
    typeof textContent === "object" &&
    textContent !== null &&
    "text" in textContent &&
    typeof (textContent as any).text === "string" &&
    (!("formatting" in textContent) ||
      Array.isArray((textContent as any).formatting))
  )
}

export function injectCssClass(
  name: string,
  props: Record<string, string | null>,
  injectedClasses: Set<string>
) {
  if (injectedClasses.has(name)) return // Prevent duplicate injections

  const css =
    `.${name} {\n` +
    Object.entries(props)
      .map(([key, val]) => `  ${_.kebabCase(key)}: ${val ?? "unset"};`)
      .join("\n") +
    "\n}"

  const styleTag = document.createElement("style")
  styleTag.innerText = css
  document.head.appendChild(styleTag)

  injectedClasses.add(name)
}
